import React, { useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScanLine, Upload, Loader2, AlertTriangle, FileDown, Check, Trash2, ClipboardPaste } from "lucide-react";
import { TOKEN_KEY, trUpper } from "../lib/mw";
import { toast } from "sonner";
import { shrinkImage } from "../lib/image";
import { Camera } from "lucide-react";

const FIELDS = [
  ["firma", "FİRMA"], ["yetkili", "YETKİLİ"], ["gsm", "GSM (CEP)"], ["s_tel", "S.TEL (SABİT)"],
  ["email", "EMAIL"], ["il", "İL"], ["ilce", "İLÇE"], ["subesi", "ŞUBE"],
];

export default function ScanCardsModal({ open, onOpenChange, onDone }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null); // review rows
  const [committing, setCommitting] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFiles([]); setItems(null); if (inputRef.current) inputRef.current.value = ""; };

  const camRef = useRef(null);
  const pick = (e) => { setFiles(Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/")).slice(0, 50)); setItems(null); };
  const snap = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setFiles((p) => [...p, f].slice(0, 50)); setItems(null); toast.success("FOTOĞRAF EKLENDİ — BAŞKA KART İÇİN TEKRAR ÇEKİN"); } };
  const onPaste = (e) => {
    const imgs = [];
    for (const it of (e.clipboardData?.items || [])) {
      if (it.type && it.type.startsWith("image/")) { const f = it.getAsFile(); if (f) imgs.push(f); }
    }
    if (imgs.length) { setFiles((p) => [...p, ...imgs].slice(0, 50)); setItems(null); toast.success(`${imgs.length} GÖRSEL YAPIŞTIRILDI`); }
  };

  const scan = async () => {
    if (!files.length) { toast.error("ÖNCE KARTVİZİT RESMİ SEÇİN VEYA YAPIŞTIRIN"); return; }
    setBusy(true);
    const fd = new FormData();
    for (const f of files) fd.append("files", await shrinkImage(f));
    try {
      const { data } = await api.post("/customers/scan-cards", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rows = (data.items || []).map((it) => ({ ...it, include: it.status !== "okunamadi", as_draft: it.status === "mukerrer" }));
      setItems(rows);
      toast.success("TARAMA BİTTİ — KONTROL EDİP ONAYLAYIN");
    } catch (e) { toast.error(e.response?.data?.detail || "TARAMA HATASI"); }
    setBusy(false);
  };

  const setField = (idx, key, val) => setItems((rows) => rows.map((r, i) => i === idx ? { ...r, data: { ...r.data, [key]: key === "email" ? val : trUpper(val) } } : r));
  const setFlag = (idx, key, val) => setItems((rows) => rows.map((r, i) => i === idx ? { ...r, [key]: val } : r));

  const commit = async () => {
    const rows = items.filter((r) => r.include && r.status !== "okunamadi").map((r) => ({ data: r.data, as_draft: !!r.as_draft, matched_firma: r.matched_firma || "" }));
    if (!rows.length) { toast.error("ONAYLANACAK KAYIT YOK"); return; }
    setCommitting(true);
    try {
      const { data } = await api.post("/customers/scan-commit", { rows });
      toast.success(`${data.added} EKLENDİ · ${data.drafts} MÜKERRER TASLAK`);
      onDone && onDone();
      reset(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "ONAY HATASI"); }
    setCommitting(false);
  };

  const downloadReport = async () => {
    const unreadable = items.filter((r) => r.status === "okunamadi").map((r) => ({ DOSYA: r.dosya, DURUM: "OKUNAMADI / FİRMA BULUNAMADI" }));
    if (!unreadable.length) return;
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    const tid = localStorage.getItem("mwcrm_tenant"); if (tid) headers["X-Tenant-Id"] = tid;
    const yr = localStorage.getItem("mwcrm_year"); if (yr) headers["X-Year"] = yr;
    const res = await fetch(`${api.defaults.baseURL}/customers/scan-report`, { method: "POST", headers, body: JSON.stringify({ data: { rows: unreadable } }) });
    if (!res.ok) { toast.error("RAPOR HATASI"); return; }
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "okunamayan_kartvizitler.xlsx"; a.click(); URL.revokeObjectURL(url);
  };

  const okItems = items ? items.filter((r) => r.status !== "okunamadi") : [];
  const badItems = items ? items.filter((r) => r.status === "okunamadi") : [];
  const chosen = okItems.filter((r) => r.include).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent onPaste={onPaste} className="max-h-[94vh] max-w-5xl overflow-y-auto border-slate-700 bg-slate-900 text-slate-100">
        <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><ScanLine className="h-5 w-5 text-indigo-400" /> KARTVİZİT TARA & ONAYLA</DialogTitle></DialogHeader>

        {!items && (
          <div className="space-y-4">
            <p className="rounded-lg border border-slate-800 bg-slate-800/40 p-3 text-xs text-slate-400">
              En fazla <b>50 kartvizit</b> seçin veya <b>Ctrl+V ile yapıştırın</b>. Taradıktan sonra bilgileri düzenleyip onaylarsınız. Resimler sisteme kaydedilmez.
            </p>
            <button type="button" data-testid="scan-camera-btn" onClick={() => camRef.current?.click()} className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-bold text-white active:scale-[0.98]"><Camera className="h-5 w-5" /> KAMERA İLE KARTVİZİT ÇEK</button>
            <input ref={camRef} data-testid="scan-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={snap} />
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-700 bg-slate-800/30 px-4 py-8 text-center hover:border-indigo-500/60">
              <Upload className="h-7 w-7 text-slate-500" />
              <span className="text-sm font-semibold text-slate-300">RESİM(LER) SEÇ</span>
              <span className="flex items-center gap-1 text-xs text-slate-500"><ClipboardPaste className="h-3.5 w-3.5" /> veya bu pencereye Ctrl+V ile yapıştırın (kes-kopyala-yapıştır)</span>
              <input ref={inputRef} data-testid="scan-file-input" type="file" accept="image/*" multiple className="hidden" onChange={pick} />
            </label>
            {files.length > 0 && <div className="text-xs text-slate-400">{files.length} görsel seçildi.</div>}
            <div className="flex justify-end">
              <Button data-testid="scan-start" onClick={scan} disabled={busy || !files.length} className="bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> TARANIYOR...</> : <><ScanLine className="mr-1 h-4 w-4" /> TARA</>}
              </Button>
            </div>
          </div>
        )}

        {items && (
          <div className="space-y-4" data-testid="scan-review">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-slate-300">{okItems.length} okundu · <b className="text-emerald-300">{chosen} seçili</b> · {badItems.length} okunamadı</div>
              {badItems.length > 0 && <Button size="sm" variant="outline" onClick={downloadReport} className="h-8 border-rose-700 bg-rose-950/30 text-rose-300"><FileDown className="mr-1 h-3.5 w-3.5" /> OKUNAMAYANLAR (EXCEL)</Button>}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {okItems.map((r) => {
                const idx = items.indexOf(r);
                return (
                  <div key={idx} data-testid={`scan-card-${idx}`} className={`rounded-xl border p-3 ${r.include ? "border-slate-700 bg-slate-800/40" : "border-slate-800 bg-slate-900/60 opacity-60"}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <label className="flex items-center gap-2 text-xs font-semibold text-slate-300">
                        <input type="checkbox" checked={r.include} onChange={(e) => setFlag(idx, "include", e.target.checked)} className="h-4 w-4 accent-indigo-500" data-testid={`scan-include-${idx}`} />
                        EKLE
                      </label>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "mukerrer" ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                        {r.status === "mukerrer" ? `MÜKERRER ≈ ${r.matched_firma}` : "YENİ"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {FIELDS.map(([k, l]) => (
                        <div key={k} className={k === "firma" || k === "email" ? "col-span-2" : ""}>
                          <div className="mb-0.5 text-[10px] uppercase text-slate-500">{l}</div>
                          <input data-testid={`scan-f-${idx}-${k}`} value={r.data[k] || ""} onChange={(e) => setField(idx, k, e.target.value)} className="h-8 w-full rounded border border-slate-700 bg-slate-800 px-2 text-xs text-slate-100" />
                        </div>
                      ))}
                    </div>
                    {r.status === "mukerrer" && (
                      <label className="mt-2 flex items-center gap-2 text-[11px] text-amber-200">
                        <input type="checkbox" checked={r.as_draft} onChange={(e) => setFlag(idx, "as_draft", e.target.checked)} className="h-3.5 w-3.5 accent-amber-500" data-testid={`scan-draft-${idx}`} />
                        TASLAK OLARAK BEKLET (işareti kaldırırsanız yine de müşteri olarak eklenir)
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-800 bg-slate-900 pt-3">
              <Button variant="outline" onClick={() => setItems(null)} className="border-slate-700 bg-slate-800 text-slate-200"><Trash2 className="mr-1 h-4 w-4" /> YENİDEN TARA</Button>
              <Button data-testid="scan-commit" onClick={commit} disabled={committing || !chosen} className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                {committing ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> EKLENİYOR...</> : <><Check className="mr-1 h-4 w-4" /> ONAYLA VE EKLE ({chosen})</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
